import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Languages } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

// ─── Types ───────────────────────────────────────────────────────────────────

interface TranslationRow {
  key: string;
  translations: { en: string; fr: string; de: string };
  createdAt: string;
  updatedAt: string;
}

interface TranslationFormState {
  key: string;
  en: string;
  fr: string;
  de: string;
}

const EMPTY_FORM: TranslationFormState = { key: "", en: "", fr: "", de: "" };
const PAGE_SIZE = 20;

// ─── API helpers ─────────────────────────────────────────────────────────────

async function fetchTranslations(): Promise<TranslationRow[]> {
  const res = await fetch("/api/admin/translations", { credentials: "include" });
  if (!res.ok) throw new Error("adminTranslations.errors.fetchFailed");
  return res.json();
}

async function createTranslation(data: {
  key: string;
  translations: { en: string; fr: string; de: string };
}): Promise<TranslationRow> {
  const res = await fetch("/api/admin/translations", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "adminTranslations.errors.unknownError" }));
    throw new Error(body.error ?? "adminTranslations.errors.createFailed");
  }
  return res.json();
}

async function updateTranslation(
  key: string,
  translations: { en: string; fr: string; de: string }
): Promise<TranslationRow> {
  const res = await fetch(`/api/admin/translations/${encodeURIComponent(key)}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ translations }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "adminTranslations.errors.unknownError" }));
    throw new Error(body.error ?? "adminTranslations.errors.updateFailed");
  }
  return res.json();
}

async function deleteTranslation(key: string): Promise<void> {
  const res = await fetch(`/api/admin/translations/${encodeURIComponent(key)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "adminTranslations.errors.unknownError" }));
    throw new Error(body.error ?? "adminTranslations.errors.deleteFailed");
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function AdminTranslations() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Search (client-side)
  const [search, setSearch] = useState("");

  // Pagination
  const [page, setPage] = useState(1);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState<TranslationFormState>(EMPTY_FORM);

  // Delete confirmation
  const [deleteKey, setDeleteKey] = useState<string | null>(null);

  // ─── Query — fetch all once ────────────────────────────────────────────────

  const { data, isLoading, isError } = useQuery<TranslationRow[]>({
    queryKey: ["/api/admin/translations"],
    queryFn: fetchTranslations,
  });

  // ─── Client-side filter + pagination ──────────────────────────────────────

  const filtered = (data ?? []).filter((row) => {
    if (search.trim() === "") return true;
    const q = search.trim().toLowerCase();
    return (
      row.key.toLowerCase().includes(q) ||
      (row.translations.en ?? "").toLowerCase().includes(q) ||
      (row.translations.fr ?? "").toLowerCase().includes(q) ||
      (row.translations.de ?? "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  // ─── Mutations ─────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: createTranslation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/translations"] });
      toast({ title: "Translation created" });
      setDialogOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, translations }: { key: string; translations: { en: string; fr: string; de: string } }) =>
      updateTranslation(key, translations),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/translations"] });
      toast({ title: "Translation updated" });
      setDialogOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTranslation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/translations"] });
      toast({ title: "Translation deleted" });
      setDeleteKey(null);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // ─── Handlers ──────────────────────────────────────────────────────────────

  function openCreateDialog() {
    setEditingKey(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEditDialog(row: TranslationRow) {
    setEditingKey(row.key);
    setForm({
      key: row.key,
      en: row.translations.en ?? "",
      fr: row.translations.fr ?? "",
      de: row.translations.de ?? "",
    });
    setDialogOpen(true);
  }

  function handleFormSubmit() {
    if (!form.key.trim()) {
      toast({ title: "Key is required", variant: "destructive" });
      return;
    }
    const translations = { en: form.en, fr: form.fr, de: form.de };
    if (editingKey !== null) {
      updateMutation.mutate({ key: editingKey, translations });
    } else {
      createMutation.mutate({ key: form.key.trim(), translations });
    }
  }

  const isMutating = createMutation.isPending || updateMutation.isPending;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">{"Translations"}</h1>
          <p className="text-slate-500 mt-2">
            {"Manage application translation keys (EN / FR / DE)"}
          </p>
        </div>
        <Button onClick={openCreateDialog} className="flex items-center gap-2 rounded-2xl bg-primary hover:bg-primary/80 font-bold shadow-[0px_4px_6px_-4px_rgba(0,68,76,0.20),0px_10px_15px_-3px_rgba(0,68,76,0.20)]">
          <Plus className="h-4 w-4" />
          {"Add Translation"}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          placeholder={"Search by key…"}
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-9 bg-slate-50 rounded-2xl"
        />
      </div>

      {/* Table */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-500">{"Failed to load translations."}</p>
      )}
      {!isLoading && !isError && (
        <div className="bg-card rounded-3xl shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
          <div className="px-6 py-5 border-b border-border/30">
            <div className="flex items-center gap-3">
              <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
                <Languages className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-foreground">
                {"Translations"}
              </h3>
            </div>
          </div>
          <div className="p-6">
            <div className="rounded-2xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[30%] text-xs font-bold text-slate-500 uppercase tracking-wide">{"Key"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">EN</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">FR</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">DE</TableHead>
                    <TableHead className="w-[100px] text-xs font-bold text-slate-500 uppercase tracking-wide text-right">{"Actions"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.length > 0 ? (
                    pageRows.map((row) => (
                      <TableRow key={row.key} className="border-t border-slate-100">
                        <TableCell className="font-mono text-xs text-slate-900">{row.key}</TableCell>
                        <TableCell className="text-sm text-slate-700">{row.translations.en}</TableCell>
                        <TableCell className="text-sm text-slate-700">{row.translations.fr}</TableCell>
                        <TableCell className="text-sm text-slate-700">{row.translations.de}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-xl"
                              onClick={() => openEditDialog(row)}
                            >
                              <Pencil className="h-3.5 w-3.5 text-primary" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => setDeleteKey(row.key)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        {search.trim() !== ""
                          ? "No keys match your search."
                          : "No translation keys yet."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between text-sm text-slate-500 mt-4">
                <span>
                  {`Showing ${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, filtered.length)} of ${filtered.length}`}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-xl"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="px-2 font-bold text-slate-700">
                    {`Page ${safePage} of ${totalPages}`}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-xl"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {editingKey !== null ? "Edit Translation" : "Add Translation"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="t-key" className="text-slate-700">{"Key"}</Label>
              <Input
                id="t-key"
                placeholder={"e.g. nav.dashboard"}
                value={form.key}
                onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                disabled={editingKey !== null}
                className="bg-slate-50 rounded-2xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-en" className="text-slate-700">{"English (EN)"}</Label>
              <Textarea
                id="t-en"
                placeholder={"English value"}
                value={form.en}
                onChange={(e) => setForm((f) => ({ ...f, en: e.target.value }))}
                className="bg-slate-50 rounded-2xl resize-none"
                rows={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-fr" className="text-slate-700">{"French (FR)"}</Label>
              <Textarea
                id="t-fr"
                placeholder={"French value"}
                value={form.fr}
                onChange={(e) => setForm((f) => ({ ...f, fr: e.target.value }))}
                className="bg-slate-50 rounded-2xl resize-none"
                rows={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-de" className="text-slate-700">{"German (DE)"}</Label>
              <Textarea
                id="t-de"
                placeholder={"German value"}
                value={form.de}
                onChange={(e) => setForm((f) => ({ ...f, de: e.target.value }))}
                className="bg-slate-50 rounded-2xl resize-none"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-2xl" onClick={() => setDialogOpen(false)}>
              {"Cancel"}
            </Button>
            <Button className="rounded-2xl" onClick={handleFormSubmit} disabled={isMutating}>
              {isMutating
                ? "Saving…"
                : editingKey !== null
                  ? "Save Changes"
                  : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteKey !== null}
        onOpenChange={(open) => { if (!open) setDeleteKey(null); }}
      >
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-slate-900">{"Delete translation key?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              {"This will permanently delete the key"}{" "}
              <span className="font-mono font-semibold text-slate-900">{deleteKey}</span>{" "}
              {"and all"}
              {" "}
              {"its translations. This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">{"Cancel"}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 rounded-2xl"
              onClick={() => deleteKey && deleteMutation.mutate(deleteKey)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
